import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth';
import { SITE_NAV, joinBetaHref } from './siteNav';
import { Icon } from './ui/Icon';

const FOCUSABLE_IN_MENU =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The public site's header (TOM-980): the approved design's bar in its fixed
 * dark palette on every public page. Below 720 px the page links and Sign in
 * move into a menu. It has no screen-look switch; the palette does not follow
 * the look. No link leads to /signup while the product is in beta (TOM-888).
 */
export function SiteHeader() {
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const betaHref = joinBetaHref(pathname);

  useEffect(() => {
    if (!navOpen) return;
    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector<HTMLElement>(FOCUSABLE_IN_MENU)?.focus();
    });
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setNavOpen(false);
      toggleRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen]);

  async function onLogout() {
    setNavOpen(false);
    await auth.logout();
    navigate('/');
  }

  const close = () => setNavOpen(false);

  return (
    <header className={`site-bar${navOpen ? ' site-bar--open' : ''}`} id="site-header">
      <nav className="site-bar__inner" aria-label="Main">
        <Link to="/" className="site-bar__brand" onClick={close}>
          <span className="site-bar__tile">
            <img src="/logo.png" alt="" width={44} height={44} />
          </span>
          <span className="site-bar__name">Neomaris</span>
        </Link>
        <div className="site-bar__right">
          <ul className="site-bar__links">
            {SITE_NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="site-bar__link">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          {auth.isAuthenticated ? (
            <div className="site-bar__auth">
              <Link to="/dashboard" className="site-bar__cta">
                <span className="site-bar__wide">Open dashboard</span>
                <span className="site-bar__narrow">Dashboard</span>
              </Link>
            </div>
          ) : (
            <div className="site-bar__auth">
              <Link to="/login" className="site-bar__link site-bar__signin">
                Sign in
              </Link>
              <Link to={betaHref} className="site-bar__cta">
                Join the beta
              </Link>
            </div>
          )}
          <button
            type="button"
            ref={toggleRef}
            className="site-bar__toggle"
            id="nav-toggle"
            aria-label="Menu"
            aria-controls="nav-menu"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            <Icon name={navOpen ? 'x' : 'menu'} size={22} />
          </button>
        </div>
      </nav>
      {navOpen ? (
        <div ref={menuRef} className="site-menu" id="nav-menu">
          {SITE_NAV.map((item) => (
            <Link key={item.to} to={item.to} className="site-menu__item" onClick={close}>
              {item.label}
            </Link>
          ))}
          <hr className="site-menu__sep" />
          {auth.isAuthenticated ? (
            <>
              <Link to="/dashboard" className="site-menu__item" onClick={close}>
                Dashboard
              </Link>
              <Link to="/profile" className="site-menu__item" onClick={close}>
                Profile
              </Link>
              <button type="button" className="site-menu__item" onClick={onLogout}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="site-menu__item" onClick={close}>
                Sign in
              </Link>
              <Link to={betaHref} className="site-menu__item" onClick={close}>
                Join the beta
              </Link>
            </>
          )}
        </div>
      ) : null}
    </header>
  );
}
